/**
 * WebMCP tool registration.
 *
 * Exposes a few of the site's own capabilities to browser AI agents, following
 * the W3C WebMCP proposal (document.modelContext). Progressive enhancement:
 * where the API is missing the module does nothing at all.
 *
 * Design rules for this site:
 * - Tools never submit a form. Every lead endpoint is behind Cloudflare
 *   Turnstile server-side (pages/ajax-submit.php), and booking a funeral
 *   advisory call is a real-world commitment, so tools open and pre-fill only.
 * - Look-up tools are marked readOnlyHint so agents can call them freely.
 *
 * Schemas live in ./schemas.js so they can be validated in CI without a DOM.
 */

import { TOOL_SCHEMAS } from './schemas.js';

const CITY_ENDPOINT = '/wp-content/themes/fenix4/pages/ajax-choice-drop.php';
const OFFICE_ENDPOINT = '/wp-content/themes/fenix4/pages/ajax-webmcp-office.php';
const PRICES_ENDPOINT = '/wp-content/themes/fenix4/pages/ajax-webmcp-prices.php';

/**
 * The 11 steps from /checklista-vid-dodsfall/. Kept here because the page body
 * is editorial WP content with no machine-readable structure to parse.
 * Update alongside the page if the checklist changes.
 */
const CHECKLIST_URL = '/checklista-vid-dodsfall/';
const CHECKLIST_STEPS = [
  'Informera anhöriga och andra berörda',
  'Ta hand om husdjur',
  'Gå igenom bostaden',
  'Beställ dödsfallsintyg med släktutredning från Skatteverket',
  'Kontakta en begravningsbyrå',
  'Leta efter testamente och nedtecknade begravningsönskemål',
  'Hantera och eftersänd posten',
  'Gå igenom försäkringar',
  'Avsluta avtal och abonnemang',
  'Kontakta bankerna: stoppa autogiron och spärra kort',
  'Övrigt: fordon, ID-handlingar, vapen och mediciner',
];

const SERVICES = {
  funeral: [
    ['Begravning med ceremoni', '/begravning-med-ceremoni/'],
    ['Begravning med ceremoni & bouppteckning', '/begravning-med-ceremoni-bouppteckning/'],
    ['Direktkremering', '/direktkremering/'],
    ['Anpassad begravning', '/anpassad-begravning/'],
    ['Kyrklig begravning', '/kyrklig-begravning/'],
    ['Borgerlig begravning', '/borgerlig-begravning/'],
    ['Alla begravningstyper', '/typer-av-begravningar/'],
    ['Transport och hämtning av avliden', '/transport-vid-begravning/'],
  ],
  legal: [
    ['Bouppteckning', '/bouppteckning/'],
    ['Arvskifte', '/arvskifte/'],
    ['Testamente', '/testamente/'],
    ['Gåvobrev', '/gavobrev/'],
    ['Skuldebrev', '/skuldebrev/'],
    ['Samboavtal', '/samboavtal/'],
    ['Äktenskapsförord', '/aktenskapsforord/'],
    ['Samäganderättsavtal', '/samaganderattsavtal/'],
  ],
};

const FALLBACK_PHONE = '020-82 82 82';
const FALLBACK_TEL_HREF = 'tel:+4620828282';

function absolute(path) {
  return new URL(path, window.location.origin).href;
}

function text(body) {
  return { content: [{ type: 'text', text: body }] };
}

/** Strip diacritics and case so "Norrkoping" matches "Norrköping". */
function normalise(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * The phone number shown to this visitor. CallTrackingMetrics rewrites these
 * spans at runtime, so reading the DOM is the only way to get the number the
 * user actually sees (a hardcoded number would break call attribution).
 */
function currentPhone() {
  const span = document.querySelector('.organiser-telnum');
  const number = span && span.textContent.trim();
  const link = span && span.closest('a[href^="tel"]');
  return {
    phone: number || FALLBACK_PHONE,
    telHref: (link && link.getAttribute('href')) || FALLBACK_TEL_HREF,
  };
}

let priceCache = null;

async function loadPrices() {
  if (priceCache) return priceCache;
  const response = await fetch(PRICES_ENDPOINT, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Price list unavailable (HTTP ${response.status})`);
  priceCache = await response.json();
  return priceCache;
}

let cityCache = null;

async function loadCities() {
  if (cityCache) return cityCache;

  const response = await fetch(CITY_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: 'select_name=webmcp_city_lookup',
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`City list unavailable (HTTP ${response.status})`);

  const markup = await response.text();
  const doc = new DOMParser().parseFromString(`<select>${markup}</select>`, 'text/html');

  cityCache = Array.from(doc.querySelectorAll('option'))
    .map((option) => ({ slug: option.value, name: option.textContent.trim() }))
    .filter((city) => city.slug);

  return cityCache;
}

function matchCities(cities, query) {
  const needle = normalise(query);
  if (!needle) return [];

  const exact = cities.filter((c) => normalise(c.name) === needle || c.slug === needle);
  if (exact.length) return exact;

  const startsWith = cities.filter((c) => normalise(c.name).startsWith(needle));
  if (startsWith.length) return startsWith;

  return cities.filter((c) => normalise(c.name).includes(needle));
}

/** Set a value the way a user would, so jQuery Validate sees the change. */
function fillField(root, selector, value) {
  if (!value) return false;
  const field = root.querySelector(selector);
  if (!field) return false;
  field.value = value;
  field.dispatchEvent(new Event('input', { bubbles: true }));
  field.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

const IMPLEMENTATIONS = {
  async getContactOptions() {
    const { phone, telHref } = currentPhone();
    return text(
      JSON.stringify(
        {
          phone,
          tel_link: telHref,
          hours: 'Telefonjour dygnet runt, alla dagar.',
          booking_page: absolute('/boka-mote/'),
          advisory_booking: 'Use the openBookingForm tool to open the callback dialog on this page.',
          note: 'This is the number shown on the current page; it may differ per region and campaign.',
        })
    );
  },

  async findLocalOffice({ city }) {
    if (!city || !String(city).trim()) {
      return text('Please provide a town or city name.');
    }

    let cities;
    try {
      cities = await loadCities();
    } catch (error) {
      return text(
        `Could not load the list of locations (${error.message}). Try the site search instead: ${absolute(
          '/search/'
        )}`
      );
    }

    const matches = matchCities(cities, city);

    if (!matches.length) {
      return text(
        `No Fenix page found for "${city}". Fenix covers ${cities.length} Swedish locations; check the spelling or browse ${absolute(
          '/vi-finns-nara-dig/'
        )}`
      );
    }

    if (matches.length > 1 && matches.length <= 10) {
      return text(
        JSON.stringify({
          ambiguous: true,
          question: `Several locations match "${city}" — which one?`,
          candidates: matches.map((c) => ({ name: c.name, url: absolute(`/begravningsbyra/${c.slug}/`) })),
        })
      );
    }

    const best = matches[0];

    let officeData = null;
    try {
      const response = await fetch(`${OFFICE_ENDPOINT}?city=${encodeURIComponent(best.slug)}`, {
        credentials: 'same-origin',
      });
      if (response.ok) officeData = await response.json();
    } catch (error) {
      // Office details are an enhancement; the page URLs are still useful without them.
    }

    return text(
      JSON.stringify({
        location: best.name,
        county: officeData ? officeData.county : undefined,
        page: absolute(`/begravningsbyra/${best.slug}/`),
        legal_services_page: absolute(`/juristbyra/${best.slug}/`),
        office: officeData && officeData.has_office ? officeData.office : undefined,
        office_note:
          officeData && !officeData.has_office
            ? 'No staffed office in this town; advisors cover it and meetings can be arranged nearby or by phone.'
            : undefined,
        contains: 'Local funeral directors, prices and contact details.',
        total_matches: matches.length,
      })
    );
  },

  async getPackagePrices({ category } = {}) {
    let data;
    try {
      data = await loadPrices();
    } catch (error) {
      return text(
        `Could not load the price list (${error.message}). Prices are shown on ${absolute(
          '/typer-av-begravningar/'
        )}`
      );
    }

    const result = { currency: 'SEK' };
    if (category !== 'legal') {
      // In "all" mode the package contents are dropped to keep output small.
      result.funeral_packages = data.funeral_packages.map((p) =>
        category === 'funeral' ? p : { name: p.name, price_sek: p.price_sek, url: p.url }
      );
      if (category !== 'funeral') {
        result.contents_note = 'Call with category "funeral" to see what each package includes.';
      }
    }
    if (category !== 'funeral') {
      result.legal_services = data.legal_services;
    }
    result.pricing_note = 'Fixed nationwide prices; null price_sek means individually quoted.';

    return text(JSON.stringify(result));
  },

  async getChecklistSteps() {
    return text(
      JSON.stringify({
        title: 'Checklista vid dödsfall — de första dagarna',
        steps: CHECKLIST_STEPS.map((step, index) => `${index + 1}. ${step}`),
        full_guide: absolute(CHECKLIST_URL),
        note: 'Fenix rådgivning är kostnadsfri och telefonjouren är öppen dygnet runt.',
      })
    );
  },

  async listServices({ category } = {}) {
    const wanted = category === 'funeral' || category === 'legal' ? [category] : ['funeral', 'legal'];
    const result = {};

    for (const key of wanted) {
      result[key] = SERVICES[key].map(([name, path]) => ({ name, url: absolute(path) }));
    }
    result.pricing_note = 'Fixed package prices are shown on each service page.';

    // Compact on purpose: Chrome budgets tool output at ~1500 chars.
    return text(JSON.stringify(result));
  },

  async openBookingForm(input = {}) {
    const modal = document.getElementById('reactForm');
    if (!modal) {
      return text(
        `There is no booking dialog on this page. The booking page is ${absolute('/boka-mote/')}`
      );
    }

    if (input.service_type === 'legal') {
      const legal = modal.querySelector('#legalInput');
      if (legal) legal.click();
    }

    const filled = [];
    if (fillField(modal, 'input[name="name"]', input.first_name)) filled.push('first name');
    if (fillField(modal, 'input[name="surname"]', input.last_name)) filled.push('surname');
    if (fillField(modal, 'input[name="phone"]', input.phone)) filled.push('phone number');

    const $ = window.jQuery;
    if ($ && typeof $(modal).modal === 'function') {
      $(modal).modal('show');
    } else {
      const trigger = document.querySelector('[data-target="#reactForm"]');
      if (trigger) trigger.click();
    }

    return text(
      [
        'Opened the "Boka rådgivning" dialog.',
        filled.length ? `Pre-filled: ${filled.join(', ')}.` : 'No details were pre-filled.',
        'It is a three-step form and includes a spam check. The user must review and submit it themselves — no booking has been made yet.',
      ].join(' ')
    );
  },
};

/** Tools that only make sense when the page actually offers them. */
function isAvailable(name) {
  if (name === 'openBookingForm') return Boolean(document.getElementById('reactForm'));
  return true;
}

function getModelContext() {
  if (typeof document !== 'undefined' && document.modelContext) return document.modelContext;
  // Chrome's origin trial shipped this on navigator before it moved to document.
  if (typeof navigator !== 'undefined' && navigator.modelContext) return navigator.modelContext;
  return null;
}

export default async function registerWebMcpTools() {
  const modelContext = getModelContext();
  if (!modelContext || typeof modelContext.registerTool !== 'function') return;

  for (const schema of TOOL_SCHEMAS) {
    const execute = IMPLEMENTATIONS[schema.name];
    if (!execute || !isAvailable(schema.name)) continue;

    try {
      await modelContext.registerTool({
        name: schema.name,
        description: schema.description,
        inputSchema: schema.inputSchema,
        annotations: schema.annotations,
        async execute(input) {
          try {
            return await execute(input || {});
          } catch (error) {
            return text(`The "${schema.name}" tool failed: ${error && error.message}`);
          }
        },
      });
    } catch (error) {
      // A single bad tool must never break the page or the remaining tools.
      if (window.console && console.warn) {
        console.warn(`WebMCP: could not register "${schema.name}"`, error);
      }
    }
  }
}
